import React, { useContext } from "react";
import { EuiBadge, EuiFlexGroup, EuiFlexItem } from "@elastic/eui";
import { type Item } from "anketa-core";
import { EvaluationContext, useAnketa } from "./context_provider";
import { countIssues } from "./issues";
import { useAnketaI18n } from "src/internationalization";

/**
 * Tab label for a section / group: plain text (headings inside a tab label
 * rendered h2-h6 sized tabs) plus a badge with what is still open in that tab,
 * so the user can find the fields to fill without opening every tab.
 */
export const TabTitle = ({ item }: { item: Item }): JSX.Element => {
  const ctx = useAnketa();
  const { translate } = useAnketaI18n();
  // Re-render (and recount) on every evaluation.
  const { revealIssues } = useContext(EvaluationContext);
  const issues = countIssues(item, ctx);

  let badge: JSX.Element | null = null;
  if (issues.invalid > 0 && revealIssues) {
    badge = (
      <EuiBadge
        color="danger"
        title={translate("anketa.questionnaire.tab.invalid", {
          values: { count: issues.invalid },
          defaultMessage: "{{count}} invalid answers",
        })}
      >
        {issues.invalid + issues.missing}
      </EuiBadge>
    );
  } else if (issues.missing > 0 || issues.invalid > 0) {
    badge = (
      <EuiBadge
        color="hollow"
        title={translate("anketa.questionnaire.tab.open", {
          values: { count: issues.missing + issues.invalid },
          defaultMessage: "{{count}} fields still open",
        })}
      >
        {issues.missing + issues.invalid}
      </EuiBadge>
    );
  }

  return (
    <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
      <EuiFlexItem grow={false}>
        {ctx.i18nContext.render(item.title)}
      </EuiFlexItem>
      {badge && <EuiFlexItem grow={false}>{badge}</EuiFlexItem>}
    </EuiFlexGroup>
  );
};
